import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../store/index';
import { Layout } from './CoreComponents';
import { fetchQuotes, sendQuoteMailFromPORequest, fetchQuoteDetail, tryToCheckout, selectQuotes, selectQuoteListError, selectIsQuoteSendingMail, selectCurrentQuote, selectIsQuoteDetailLoading, selectQuoteDetailError } from '../store/slices/quoteSlice';
import { deleteQuoteItem } from '../store/slices/quoteItemSlice';
import ConfirmationModal from './Shared/ConfirmationModal';
import { formatDate, formatTime } from '../utils/formatterDate';
import { QuoteDto } from '../generated';
import { useToast } from '../contexts/ToastContext';
import { AgGridReact } from 'ag-grid-react';
import { type ColDef, type ICellRendererParams } from 'ag-grid-community';
import BaseGrid from './Shared/BaseGrid';

interface QuoteEvaluationScreenProps {}

interface Quote {
  id: string;
  quoteNumber: string;
  supplierName: string;
  supplierEmail: string;
  quantity: string;
  productName: string;
  price: number;
  sendDate: string;
  sendTime: string;
  answerDate: string;
  answerTime: string;
  targetDuration: number;
  status: 'created' | 'emailSent' | 'emailOpened' | 'responded';
  /** ISO date string from API (e.g. "2026-03-10T15:28:32.210949") for reliable calculation */
  emailSentAt?: string;
  /** ISO date string from API for reliable calculation */
  respondedAt?: string;
}

function parseQuoteMailDisplayDate(dateStr: string): Date | null {
  if (!dateStr || !dateStr.trim()) return null;
  const normalized = dateStr.trim();
  if (normalized.includes('/')) {
    const parts = normalized.split('/');
    if (parts.length === 3) {
      const iso = `${parts[2]}-${parts[1]}-${parts[0]}`;
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  if (normalized.includes('.')) {
    const iso = normalized.split('.').reverse().join('-');
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function calculateQuoteElapsedTime(sendDate: string, answerDate: string, emailSentAt?: string, respondedAt?: string) {
  const send = emailSentAt ? new Date(emailSentAt) : parseQuoteMailDisplayDate(sendDate);
  if (!send || Number.isNaN(send.getTime())) return '-';
  if (!answerDate && !respondedAt) {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - send.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} gün`;
  }
  const answer = respondedAt ? new Date(respondedAt) : parseQuoteMailDisplayDate(answerDate);
  if (!answer || Number.isNaN(answer.getTime())) return '-';
  const diffTime = Math.abs(answer.getTime() - send.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return `${diffDays} gün`;
}

function calculateQuoteLastDate(sendDate: string, emailSentAt?: string, targetDuration: number = 7) {
  const send = emailSentAt ? new Date(emailSentAt) : parseQuoteMailDisplayDate(sendDate);
  if (!send || Number.isNaN(send.getTime())) return '-';
  const days = Math.max(0, targetDuration);
  const lastDate = new Date(send.getTime() + (days * 24 * 60 * 60 * 1000));
  return formatDate(lastDate);
}

function getQuoteMailStatusInfo(status: string) {
  switch (status) {
    case 'created':
      return {
        text: 'Teklif Oluşturuldu',
        color: 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300',
        icon: 'fas fa-file-alt'
      };
    case 'emailSent':
      return {
        text: 'Teklif Gönderildi',
        color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        icon: 'fas fa-envelope'
      };
    case 'responded':
      return {
        text: 'Teklif Cevaplandı',
        color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
        icon: 'fas fa-check'
      };
    case 'emailOpened':
      return {
        text: 'Teklif Açıldı',
        color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        icon: 'fas fa-eye'
      };
    default:
      return { text: status, color: 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300', icon: 'fas fa-question' };
  }
}

const QuoteTrackingPage: React.FC<QuoteEvaluationScreenProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showError, showSuccess } = useToast();
  const gridRef = useRef<AgGridReact>(null);
  const quotes = useSelector(selectQuotes);
  const error = useSelector(selectQuoteListError);
  const isSendingMail = useSelector(selectIsQuoteSendingMail);
  const currentQuoteDetail = useSelector(selectCurrentQuote);
  const isDetailLoading = useSelector(selectIsQuoteDetailLoading);
  const detailError = useSelector(selectQuoteDetailError);

  const [searchTerm, setSearchTerm] = useState('');
  const [showExpired, setShowExpired] = useState(false);
  const [showUnanswered, setShowUnanswered] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showResendModal, setShowResendModal] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [selectedQuote, setSelectedQuote] = useState<string>('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedQuoteForDetail, setSelectedQuoteForDetail] = useState<string>('');
  const [showQuoteItemDeleteConfirmation, setShowQuoteItemDeleteConfirmation] = useState(false);
  const [quoteItemToDelete, setQuoteItemToDelete] = useState<{ id: number; itemLabel?: string } | null>(null);
  const [showCheckoutConfirmation, setShowCheckoutConfirmation] = useState(false);

  const isQuoteItemDeleteLoading = useSelector((state: RootState) => state.quoteItem.operationLoading.delete);
  const isCheckoutLoading = useSelector((state: RootState) => state.quote.loading.checkout);

  // Fetch quotes on component mount
  useEffect(() => {
    dispatch(fetchQuotes({}));
  }, [dispatch]);

  // Fetch quote detail when detail modal opens
  useEffect(() => {
    if (showDetailModal && selectedQuoteForDetail) {
      dispatch(fetchQuoteDetail({ id: parseInt(selectedQuoteForDetail, 10) }));
    }
  }, [showDetailModal, selectedQuoteForDetail, dispatch]);


  // Show error toast when error state changes
  useEffect(() => {
    if (error) {
      showError(error);
    }
  }, [error, showError]);

  // Transform API data to component format
  const transformQuoteData = (apiQuote: QuoteDto): Quote => {


    // Determine status based on API fields
    let status: 'created' | 'emailSent' | 'emailOpened' | 'responded' = 'created';
    if (apiQuote.responded) {
      status = 'responded';
    } else if (apiQuote.emailOpened) {
      status = 'emailOpened';
    } else if (apiQuote.emailSentAt) {
      status = 'emailSent';
    }

    // Calculate total quantity and price from quoteItems
    const totalQuantity = apiQuote.quoteItems?.reduce((sum, item) => sum + (item.negotiationQuantity || item.offeredQuantity || 0), 0) || 0;
    const totalPrice = apiQuote.quoteItems?.reduce((sum, item) => {
      const quantity = item.negotiationQuantity || item.offeredQuantity || 0;
      const price = item.negotiationPrice || item.offeredPrice || 0;
      return sum + (quantity * price);
    }, 0) || 0;

    return {
      id: apiQuote.id?.toString() || '',
      quoteNumber: `${apiQuote.id}`,
      supplierName: apiQuote.supplierName || '',
      supplierEmail: apiQuote.supplierEmail || '',
      quantity: totalQuantity.toString(),
      productName: apiQuote.quoteItems?.[0]?.itemName || '',
      price: totalPrice,
      sendDate: apiQuote.emailSentAt ? formatDate(apiQuote.emailSentAt) : '',
      sendTime: apiQuote.emailSentAt ? formatTime(apiQuote.emailSentAt) : '',
      answerDate: apiQuote.respondedAt ? formatDate(apiQuote.respondedAt) : '',
      answerTime: apiQuote.respondedAt ? formatTime(apiQuote.respondedAt) : '',
      targetDuration: apiQuote.timeoutDays || 7,
      status,
      ...(apiQuote.emailSentAt != null && {
        emailSentAt: typeof apiQuote.emailSentAt === 'string' ? apiQuote.emailSentAt : apiQuote.emailSentAt.toISOString()
      }),
      ...(apiQuote.respondedAt != null && {
        respondedAt: typeof apiQuote.respondedAt === 'string' ? apiQuote.respondedAt : apiQuote.respondedAt.toISOString()
      })
    };
  };

  // Transform quotes data
  const transformedQuotes = quotes.map(transformQuoteData);

  const handleResendMail = (quoteId: string) => {
    const quote = transformedQuotes.find(q => q.id === quoteId);
    if (quote) {
      setSelectedQuote(quoteId);
      setResendEmail(quote.supplierEmail);
      setShowResendModal(true);
    }
  };

  const handleSendMail = async () => {
    if (selectedQuote) {
      try {
        const result = await dispatch(sendQuoteMailFromPORequest({ 
          id: parseInt(selectedQuote),
          data: { supplierEmail: resendEmail }
        }));
        if (sendQuoteMailFromPORequest.fulfilled.match(result)) {
          showSuccess('Teklif maili başarıyla gönderildi!');
          setShowResendModal(false);
        } else {
          showError('Mail gönderilirken bir hata oluştu.');
        }
      } catch (error) {
        console.error('Error sending mail:', error);
        showError('Mail gönderilirken bir hata oluştu.');
      }
    }
  };

  const handleCloseModal = () => {
    setShowResendModal(false);
  };

  const handleOpenDetail = (quoteId: string) => {
    setSelectedQuoteForDetail(quoteId);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedQuoteForDetail('');
  };

  const openQuoteItemDeleteConfirmation = (id: number, itemLabel?: string) => {
    setQuoteItemToDelete(itemLabel !== undefined ? { id, itemLabel } : { id });
    setShowQuoteItemDeleteConfirmation(true);
  };

  const confirmQuoteItemDelete = async () => {
    if (!quoteItemToDelete || !selectedQuoteForDetail) return;
    try {
      const deleteResult = await dispatch(deleteQuoteItem(quoteItemToDelete.id)).unwrap();
      showSuccess(deleteResult.message || 'Teklif kalemi silindi.');
      setShowQuoteItemDeleteConfirmation(false);
      setQuoteItemToDelete(null);
      dispatch(fetchQuoteDetail({ id: parseInt(selectedQuoteForDetail, 10) }));
    } catch (err) {
      showError('Teklif kalemi silinirken bir hata oluştu.');
    }
  };

  const confirmCheckout = async () => {
    if (!selectedQuoteForDetail) return;
    try {
      await dispatch(tryToCheckout(parseInt(selectedQuoteForDetail, 10))).unwrap();
      showSuccess('Tetiği çekme işlemi tamamlandı.');
      setShowCheckoutConfirmation(false);
      dispatch(fetchQuoteDetail({ id: parseInt(selectedQuoteForDetail, 10) }));
    } catch (err) {
      showError('Tetiği çekme işlemi başarısız.');
    }
  };

  const filteredQuotes = useMemo(() => {
    return transformedQuotes.filter(quote => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesQuoteNo = (quote.quoteNumber || '').toLowerCase().includes(term);
        const matchesSupplierName = (quote.supplierName || '').toLowerCase().includes(term);
        if (!matchesQuoteNo && !matchesSupplierName) return false;
      }
      if (statusFilter !== 'all' && quote.status !== statusFilter) return false;
      if (showUnanswered && quote.status === 'responded') return false;
      if (!showExpired) return true;
      if (quote.answerDate) return true;
      const sendTime = quote.emailSentAt
        ? new Date(quote.emailSentAt).getTime()
        : (() => {
            const s = quote.sendDate;
            if (!s || !s.trim()) return NaN;
            const parts = s.includes('/') ? s.trim().split('/') : s.trim().split('.');
            if (parts.length !== 3) return NaN;
            const t = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime();
            return Number.isNaN(t) ? NaN : t;
          })();
      if (Number.isNaN(sendTime)) return true;
      const now = new Date();
      const diffDays = Math.ceil(Math.abs(now.getTime() - sendTime) / (1000 * 60 * 60 * 24));
      return diffDays > quote.targetDuration;
    });
  }, [transformedQuotes, searchTerm, statusFilter, showUnanswered, showExpired]);

  const handleOpenDetailCb = useCallback((quoteId: string) => {
    setSelectedQuoteForDetail(quoteId);
    setShowDetailModal(true);
  }, []);

  const handleResendMailCb = useCallback((quoteId: string) => {
    const quote = transformedQuotes.find(q => q.id === quoteId);
    if (quote) {
      setSelectedQuote(quoteId);
      setResendEmail(quote.supplierEmail);
      setShowResendModal(true);
    }
  }, [transformedQuotes]);

  const columnDefs = useMemo<ColDef<Quote>[]>(() => [
    {
      headerName: 'Teklif No',
      field: 'quoteNumber',
      minWidth: 90,
      flex: 0.7,
      comparator: (_a: any, _b: any, nodeA: any, nodeB: any) =>
        (parseInt(nodeA.data?.quoteNumber) || 0) - (parseInt(nodeB.data?.quoteNumber) || 0),
    },
    {
      headerName: 'Tedarikçi',
      field: 'supplierName',
      minWidth: 160,
      flex: 1.5,
      tooltipField: 'supplierName',
      cellRenderer: (params: ICellRendererParams<Quote>) => (
        <div>
          <div className="truncate">{params.data?.supplierName}</div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{params.data?.supplierEmail}</div>
        </div>
      ),
    },
    {
      headerName: 'Gönderim',
      field: 'sendDate',
      minWidth: 100,
      flex: 0.8,
      cellRenderer: (params: ICellRendererParams<Quote>) => (
        <div className="text-center">
          <div>{params.data?.sendDate || '-'}</div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400">{params.data?.sendTime || ''}</div>
        </div>
      ),
      comparator: (_a: any, _b: any, nodeA: any, nodeB: any) => {
        const getTime = (q: Quote | undefined) => q?.emailSentAt ? new Date(q.emailSentAt).getTime() : 0;
        return getTime(nodeA.data) - getTime(nodeB.data);
      },
    },
    {
      headerName: 'Geçen Süre',
      minWidth: 90,
      flex: 0.7,
      sortable: false,
      filter: false,
      valueGetter: (params: any) => {
        const q = params.data as Quote;
        return q ? calculateQuoteElapsedTime(q.sendDate, q.answerDate, q.emailSentAt, q.respondedAt) : '-';
      },
    },
    {
      headerName: 'Hedef',
      field: 'targetDuration',
      minWidth: 70,
      flex: 0.5,
      valueFormatter: (params: any) => `${params.value} gün`,
    },
    {
      headerName: 'Son Tarih',
      minWidth: 100,
      flex: 0.8,
      valueGetter: (params: any) => {
        const q = params.data as Quote;
        return q ? calculateQuoteLastDate(q.sendDate, q.emailSentAt, q.targetDuration) : '-';
      },
    },
    {
      headerName: 'Cevaplanma',
      field: 'answerDate',
      minWidth: 100,
      flex: 0.8,
      cellRenderer: (params: ICellRendererParams<Quote>) => (
        <div className="text-center">
          <div>{params.data?.answerDate || '-'}</div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400">{params.data?.answerTime || ''}</div>
        </div>
      ),
      comparator: (_a: any, _b: any, nodeA: any, nodeB: any) => {
        const getTime = (q: Quote | undefined) => q?.respondedAt ? new Date(q.respondedAt).getTime() : 0;
        return getTime(nodeA.data) - getTime(nodeB.data);
      },
    },
    {
      headerName: 'Durum',
      field: 'status',
      minWidth: 130,
      flex: 0.9,
      cellRenderer: (params: ICellRendererParams<Quote>) => {
        if (!params.data) return null;
        const info = getQuoteMailStatusInfo(params.data.status);
        return (
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${info.color}`}>
            <i className={`mr-1 ${info.icon} flex-shrink-0`}></i>
            <span className="truncate">{info.text}</span>
          </span>
        );
      },
    },
    {
      headerName: 'Not',
      minWidth: 100,
      flex: 0.8,
      valueGetter: (params: any) => (params.data as any)?.note ?? (params.data as any)?.requestNote ?? '',
      valueFormatter: (params: any) => params.value || '-',
      tooltipValueGetter: (params: any) => params.value || '',
    },
    {
      headerName: 'İşlemler',
      sortable: false,
      filter: false,
      resizable: false,
      minWidth: 180,
      flex: 1.2,
      cellRenderer: (params: ICellRendererParams<Quote>) => {
        if (!params.data) return null;
        const q = params.data;
        return (
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleOpenDetailCb(q.id)}
              className="px-2 py-0.5 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors flex items-center gap-0.5 text-xs"
            >
              <i className="fas fa-info-circle text-[10px]"></i>
              <span>Detay</span>
            </button>
            {q.status === 'created' ? (
              <button
                onClick={() => handleResendMailCb(q.id)}
                className="px-2 py-0.5 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center gap-0.5 text-xs"
              >
                <i className="fas fa-envelope text-[10px]"></i>
                <span>Mail Gönder</span>
              </button>
            ) : (q.status === 'emailSent' || q.status === 'emailOpened') ? (
              <button
                onClick={() => handleResendMailCb(q.id)}
                className="px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-0.5 text-xs"
              >
                <i className="fas fa-redo text-[10px]"></i>
                <span>Yeniden Gönder</span>
              </button>
            ) : (
              <button
                disabled
                className="px-2 py-0.5 bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 rounded cursor-not-allowed flex items-center gap-0.5 text-xs"
              >
                <i className="fas fa-redo text-[10px]"></i>
                <span>Yeniden Gönder</span>
              </button>
            )}
          </div>
        );
      },
    },
  ], [handleOpenDetailCb, handleResendMailCb]);

  const getDecisionStatusLabel = (status: string | null | undefined): string => {
    if (!status) return '—';
    const s = status.toString();
    switch (s) {
      case 'NotDecided':
        return 'Karar verilmedi';
      case 'Awarded':
        return 'Onaylandı';
      case 'Rejected':
      case 'rejected':
        return 'Reddedildi';
      case 'Negotiate':
      case 'negotiate':
        return 'Müzakere';
      default:
        return s;
    }
  };

  return (
    <Layout currentScreen="quote-tracking">
      <div className="max-w-8xl mx-auto h-full flex flex-col">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4 transition-colors duration-300 flex-shrink-0">
          <div className="flex flex-col gap-4">
            <div className="flex items-end gap-3">
            {/* Search bar - sola bitişik, yenileye kadar uzanıyor */}
            <div className="flex-1 relative">
              <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500"></i>
              <input 
                type="text" 
                placeholder="Teklif No veya Tedarikçi Ara..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 text-sm"
              />
            </div>
            {/* Yenile - sağa yanaşık */}
            <button 
              onClick={() => dispatch(fetchQuotes({}))}
              className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex-shrink-0"
            >
              <i className="fas fa-sync-alt mr-2"></i>Yenile
            </button>
            </div>
            
            {/* Filter Controls and Action Buttons Row */}
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
              {/* Filter Controls - Segmented Control Style */}
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Status Filter */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Durum:</span>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="all">Tümü</option>
                    <option value="created">Oluşturuldu</option>
                    <option value="emailSent">Gönderildi</option>
                    <option value="emailOpened">Açıldı</option>
                    <option value="responded">Cevaplandı</option>
                  </select>
                </div>

                {/* Other Filters */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Diğer:</span>
                  <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 p-1">
                    <button
                      onClick={() => {
                        setShowExpired(false);
                        setShowUnanswered(false);
                      }}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                        !showExpired && !showUnanswered
                          ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                      }`}
                    >
                      Tümü
                    </button>
                    <button
                      onClick={() => setShowExpired(!showExpired)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                        showExpired
                          ? 'bg-white dark:bg-gray-600 text-orange-600 dark:text-orange-400 shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                      }`}
                    >
                      Süresi Geçenler
                    </button>
                    <button
                      onClick={() => setShowUnanswered(!showUnanswered)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                        showUnanswered
                          ? 'bg-white dark:bg-gray-600 text-red-600 dark:text-red-400 shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                      }`}
                    >
                      Cevaplanmayanlar
                    </button>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 transition-colors duration-300 flex flex-col flex-1 min-h-0 max-h-[calc(100vh-300px)] min-h-[300px]">
          {/* Desktop AG Grid */}
          <div className="hidden lg:flex flex-col flex-1 min-h-0">
            <div className="flex-1 min-h-0 relative" style={{ minHeight: '300px' }}>
              <div className="absolute inset-0">
                <BaseGrid<Quote>
                  ref={gridRef}
                  gridStateKey="ag-grid-quote-mail-tracking"
                  rowData={filteredQuotes}
                  columnDefs={columnDefs}
                  getRowId={(params) => params.data.id}
                  autoSizeStrategy={{ type: 'fitGridWidth' }}
                  domLayout="normal"
                />
              </div>
            </div>
          </div>

          {/* Mobile Card Layout */}
          <div className="lg:hidden overflow-y-auto">
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredQuotes.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <i className="fas fa-inbox text-4xl text-gray-400 dark:text-gray-500 mb-4"></i>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Henüz teklif bulunmuyor</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {searchTerm ? 'Arama kriterlerinize uygun teklif bulunamadı.' : 'Sistemde henüz teklif kaydı bulunmuyor.'}
                    </p>
                  </div>
                </div>
              ) : (
                filteredQuotes.map((quote) => {
                  const statusInfo = getQuoteMailStatusInfo(quote.status);
                  return (
                    <div key={quote.id} className="pl-3 sm:pl-6 pr-0 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                              {quote.quoteNumber}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Teklif No: {quote.quoteNumber}
                            </p>
                          </div>
                          <div className="flex flex-col items-end space-y-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                              <i className={`mr-1 ${statusInfo.icon}`}></i>
                              <span className="truncate">{statusInfo.text}</span>
                            </span>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleOpenDetail(quote.id)}
                                className="px-2 py-0.5 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors flex items-center gap-0.5 text-xs"
                              >
                                <i className="fas fa-info-circle text-[10px]"></i>
                                <span>Detay</span>
                              </button>
                              {quote.status === 'created' ? (
                                <button
                                  onClick={() => handleResendMail(quote.id)}
                                  className="px-2 py-0.5 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center gap-0.5 text-xs"
                                >
                                  <i className="fas fa-envelope text-[10px]"></i>
                                  <span>Mail Gönder</span>
                                </button>
                              ) : (quote.status === 'emailSent' || quote.status === 'emailOpened') ? (
                                <button
                                  onClick={() => handleResendMail(quote.id)}
                                  className="px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-0.5 text-xs"
                                >
                                  <i className="fas fa-redo text-[10px]"></i>
                                  <span>Yeniden Gönder</span>
                                </button>
                              ) : (
                                <button
                                  disabled
                                  className="px-2 py-0.5 bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 rounded cursor-not-allowed flex items-center gap-0.5 text-xs"
                                >
                                  <i className="fas fa-redo text-[10px]"></i>
                                  <span>Yeniden Gönder</span>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Tedarikçi:</span>
                            <div className="text-right">
                              <div className="text-sm text-gray-700 dark:text-gray-300">{quote.supplierName}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{quote.supplierEmail}</div>
                            </div>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Gönderim Tarihi:</span>
                            <div className="text-right">
                              <div className="text-sm text-gray-700 dark:text-gray-300">{quote.sendDate}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{quote.sendTime}</div>
                            </div>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Geçen Süre:</span>
                            <span className="text-sm text-gray-700 dark:text-gray-300">{calculateQuoteElapsedTime(quote.sendDate, quote.answerDate, quote.emailSentAt, quote.respondedAt)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Hedef Süre:</span>
                            <span className="text-sm text-gray-700 dark:text-gray-300">{quote.targetDuration} gün</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Son Tarih:</span>
                            <span className="text-sm text-gray-700 dark:text-gray-300">{calculateQuoteLastDate(quote.sendDate, quote.emailSentAt, quote.targetDuration)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Cevap Tarihi:</span>
                            <div className="text-right">
                              <div className="text-sm text-gray-700 dark:text-gray-300">{quote.answerDate || '-'}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{quote.answerTime || '-'}</div>
                            </div>
                          </div>
                          {((quote as any).note ?? (quote as any).requestNote) && (
                            <div className="flex justify-between">
                              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Not:</span>
                              <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[200px]" title={(quote as any).note ?? (quote as any).requestNote}>{(quote as any).note ?? (quote as any).requestNote}</span>
                            </div>
                          )}
                        </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

       {/* Resend Mail Modal */}
       {showResendModal && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
           <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 transition-colors duration-300">
             <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
               <div className="flex items-center justify-between">
                 <h3 className="text-lg font-semibold text-gray-900 dark:text-white transition-colors">
                   Yeniden Gönder
                 </h3>
                 <button
                   onClick={handleCloseModal}
                   className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                 >
                   <i className="fas fa-times text-xl"></i>
                 </button>
               </div>
             </div>
             
             <div className="px-6 py-4">
               <div className="mb-4">
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">
                   Tedarikçi E-posta Adresi
                 </label>
                 <input
                   type="email"
                   value={resendEmail}
                   onChange={(e) => setResendEmail(e.target.value)}
                   className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                   placeholder="Tedarikçi e-posta adresini girin"
                 />
               </div>
               
                 <div className="mb-6">
                   <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors">
                     <strong>1</strong> adet teklif için mail gönderilecek.
                   </p>
                   {transformedQuotes.find(q => q.id === selectedQuote) && (
                     <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                       <p className="text-xs text-gray-600 dark:text-gray-400">
                         <strong>Teklif:</strong> {transformedQuotes.find(q => q.id === selectedQuote)?.quoteNumber}
                       </p>
                       <p className="text-xs text-gray-600 dark:text-gray-400">
                         <strong>Tedarikçi:</strong> {transformedQuotes.find(q => q.id === selectedQuote)?.supplierName}
                       </p>
                     </div>
                   )}
                 </div>
             </div>
             
             <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-600 flex justify-end space-x-3">
               <button
                 onClick={handleCloseModal}
                 className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
               >
                 İptal
               </button>
               <button
                 onClick={handleSendMail}
                 disabled={isSendingMail}
                 className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
               >
                 {isSendingMail ? (
                   <>
                     <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                     <span>Gönderiliyor...</span>
                   </>
                 ) : (
                   <>
                     <i className="fas fa-envelope"></i>
                     <span>Gönder</span>
                   </>
                 )}
               </button>
             </div>
           </div>
         </div>
       )}

       {/* Detay Modal */}
       {showDetailModal && (
         <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="quote-detail-title">
           <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-7xl w-full max-h-[95vh] flex flex-col transition-all duration-200 overflow-hidden">
             {/* Header */}
             <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/80">
               <div className="flex items-center justify-between gap-4">
                 <div className="flex items-center gap-3 min-w-0">
                   <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
                     <i className="fas fa-file-invoice text-primary"></i>
                   </div>
                   <div className="min-w-0">
                     <h2 id="quote-detail-title" className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                       Teklif Detayı {currentQuoteDetail?.id && Number(selectedQuoteForDetail) === currentQuoteDetail.id ? `#${currentQuoteDetail.id}` : ''}
                     </h2>
                   </div>
                 </div>
                 <div className="flex items-center gap-2">
                   {currentQuoteDetail &&
                     Number(selectedQuoteForDetail) === currentQuoteDetail.id &&
                     !!currentQuoteDetail.respondedAt &&
                     (currentQuoteDetail.quoteItems ?? []).length > 0 &&
                     (currentQuoteDetail.quoteItems ?? []).every((item) => item.decisionStatus != null && item.decisionStatus !== 'NotDecided') && (
                       <button
                         type="button"
                         onClick={() => setShowCheckoutConfirmation(true)}
                         disabled={isCheckoutLoading}
                         className="flex-shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                       >
                         <i className="fas fa-play text-xs"></i>
                         <span>Tetiği Çek</span>
                       </button>
                     )}
                   <button
                     type="button"
                     onClick={handleCloseDetailModal}
                     className="flex-shrink-0 p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 dark:text-gray-400 dark:hover:text-white transition-colors"
                     aria-label="Kapat"
                   >
                     <i className="fas fa-times text-lg"></i>
                   </button>
                 </div>
               </div>
             </div>

             {/* Body */}
             <div className="flex-1 overflow-y-auto min-h-0">
               {isDetailLoading && (
                 <div className="flex flex-col items-center justify-center py-16 gap-4">
                   <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary border-t-transparent"></div>
                   <p className="text-sm text-gray-500 dark:text-gray-400">Teklif bilgileri yükleniyor...</p>
                 </div>
               )}
               {!isDetailLoading && detailError && (
                 <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                   <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-3">
                     <i className="fas fa-exclamation-circle text-red-600 dark:text-red-400 text-xl"></i>
                   </div>
                   <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Yükleme hatası</p>
                   <p className="text-sm text-red-600 dark:text-red-400">{detailError}</p>
                 </div>
               )}
               {!isDetailLoading && !detailError && currentQuoteDetail && Number(selectedQuoteForDetail) === currentQuoteDetail.id && (
                 <div className="p-6 space-y-6">
                   {/* Tedarikçi bilgileri + Süreç + Notlar - aynı satırda compact */}
                   <div className={`grid grid-cols-1 gap-4 ${(currentQuoteDetail.noteToSupplier || currentQuoteDetail.noteFromSupplier) ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
                     {/* Kart: Tedarikçi bilgileri */}
                     <div className="rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-700/30 p-3 sm:p-4">
                       <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-2">
                         <i className="fas fa-building text-gray-400 text-[10px]"></i>
                         Tedarikçi bilgileri
                       </h3>
                       <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                         <div>
                           <dt className="text-gray-500 dark:text-gray-400 font-normal">Tedarikçi ismi</dt>
                           <dd className="font-medium text-gray-900 dark:text-white truncate" title={currentQuoteDetail.supplierName || ''}>{currentQuoteDetail.supplierName || '—'}</dd>
                         </div>
                         <div>
                           <dt className="text-gray-500 dark:text-gray-400 font-normal">Mail</dt>
                           <dd className="font-medium text-gray-900 dark:text-white truncate text-[11px]" title={currentQuoteDetail.supplierEmail || ''}>{currentQuoteDetail.supplierEmail || '—'}</dd>
                         </div>
                         <div>
                           <dt className="text-gray-500 dark:text-gray-400 font-normal">Zaman aşımı</dt>
                           <dd className="font-medium text-gray-900 dark:text-white">{currentQuoteDetail.timeoutDays != null ? `${currentQuoteDetail.timeoutDays} gün` : '—'}</dd>
                         </div>
                         <div>
                           <dt className="text-gray-500 dark:text-gray-400 font-normal">Müzakereli teklif</dt>
                           <dd className="font-medium text-gray-900 dark:text-white">{currentQuoteDetail.isNegotiate ? 'Evet' : 'Hayır'}</dd>
                         </div>
                       </dl>
                     </div>

                     {/* Kart: Süreç */}
                     <div className="rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-700/30 p-3 sm:p-4">
                       <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-2">
                         <i className="fas fa-clock-rotate-left text-gray-400 text-[10px]"></i>
                         Süreç
                       </h3>
                       <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                         <div className="flex items-center gap-2 min-w-0">
                           <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                             <i className="fas fa-paper-plane text-blue-600 dark:text-blue-400 text-[10px]"></i>
                           </div>
                           <div className="min-w-0">
                             <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium">Mail gönderildi</p>
                             <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                               {currentQuoteDetail.emailSentAt ? `${formatDate(String(currentQuoteDetail.emailSentAt))} ${formatTime(String(currentQuoteDetail.emailSentAt))}` : '—'}
                             </p>
                           </div>
                         </div>
                         <i className="fas fa-chevron-right text-gray-400 dark:text-gray-500 text-xs flex-shrink-0" aria-hidden />
                         <div className="flex items-center gap-2 min-w-0">
                           <div className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                             <i className="fas fa-envelope-open text-amber-600 dark:text-amber-400 text-[10px]"></i>
                           </div>
                           <div className="min-w-0">
                             <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium">Mail açıldı</p>
                             <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                               {currentQuoteDetail.emailOpenedAt ? `${formatDate(String(currentQuoteDetail.emailOpenedAt))} ${formatTime(String(currentQuoteDetail.emailOpenedAt))}` : '—'}
                             </p>
                           </div>
                         </div>
                         <i className="fas fa-chevron-right text-gray-400 dark:text-gray-500 text-xs flex-shrink-0" aria-hidden />
                         <div className="flex items-center gap-2 min-w-0">
                           <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                             <i className="fas fa-reply text-emerald-600 dark:text-emerald-400 text-[10px]"></i>
                           </div>
                           <div className="min-w-0">
                             <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium">Cevaplandı</p>
                             <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                               {currentQuoteDetail.respondedAt ? `${formatDate(String(currentQuoteDetail.respondedAt))} ${formatTime(String(currentQuoteDetail.respondedAt))}` : '—'}
                             </p>
                           </div>
                         </div>
                       </div>
                     </div>

                     {/* Kart: Notlar - sadece varsa */}
                     {(currentQuoteDetail.noteToSupplier || currentQuoteDetail.noteFromSupplier) && (
                       <div className="rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50/50 dark:bg-amber-900/10 p-3 sm:p-4">
                         <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-800 dark:text-amber-300 mb-3 flex items-center gap-2">
                           <i className="fas fa-comment-dots text-amber-600 dark:text-amber-400 text-[10px]"></i>
                           Notlar
                         </h3>
                         <div className="space-y-3 text-xs">
                           {currentQuoteDetail.noteToSupplier && (
                             <div>
                               <p className="text-[10px] font-medium text-amber-800/80 dark:text-amber-300/80 mb-0.5">Tedarikçiye not</p>
                               <p className="text-gray-800 dark:text-gray-200 leading-relaxed line-clamp-3">{currentQuoteDetail.noteToSupplier}</p>
                             </div>
                           )}
                           {currentQuoteDetail.noteFromSupplier && (
                             <div>
                               <p className="text-[10px] font-medium text-amber-800/80 dark:text-amber-300/80 mb-0.5">Tedarikçiden not</p>
                               <p className="text-gray-800 dark:text-gray-200 leading-relaxed line-clamp-3">{currentQuoteDetail.noteFromSupplier}</p>
                             </div>
                           )}
                         </div>
                       </div>
                     )}
                   </div>

                   {/* Teklif kalemleri tablosu - koşullu kolonlar, kendi içinde scroll, compact */}
                   {(() => {
                     const isResponded = !!currentQuoteDetail.respondedAt;
                     const isNegotiate = !!currentQuoteDetail.isNegotiate;
                     // "Talep" ve "Teklif" kolonları QuoteItem.Requested* alanlarından,
                     // "Müzakere" kolonları ise tedarikçinin cevabını taşıyan QuoteItem.Offered* alanlarından beslenir.
                     const showOfferedColumns = isResponded || isNegotiate;
                     const showNegotiationColumns = isResponded;
                     const showDecisionColumn = isResponded;
                     return (
                   <div className="rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden flex flex-col min-h-0">
                     <div className="flex-shrink-0 px-3 py-2 bg-gray-100 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                       <h3 className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                         <i className="fas fa-list-ul text-gray-500 text-[10px]"></i>
                         Teklif kalemleri
                         <span className="font-normal normal-case text-gray-500 dark:text-gray-400">
                           ({(currentQuoteDetail.quoteItems ?? []).length} adet)
                         </span>
                       </h3>
                     </div>
                     <div className="flex-1 min-h-0 overflow-auto max-h-[280px]">
                       <table className="w-full text-xs border-collapse">
                         <thead className="bg-gray-50 dark:bg-gray-700/70 sticky top-0 z-10">
                           <tr>
                             <th scope="col" className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Talep No</th>
                             <th scope="col" className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Ürün</th>
                             <th scope="col" className="px-2 py-1.5 text-right text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Talep miktar</th>
                             {showOfferedColumns && (
                               <>
                                 <th scope="col" className="px-2 py-1.5 text-right text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Teklif miktar</th>
                                 <th scope="col" className="px-2 py-1.5 text-right text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Teklif fiyat</th>
                                 <th scope="col" className="px-2 py-1.5 text-center text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Teslimat (gün)</th>
                               </>
                             )}
                             {showNegotiationColumns && (
                               <>
                                 <th scope="col" className="px-2 py-1.5 text-right text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Müz. miktar</th>
                                 <th scope="col" className="px-2 py-1.5 text-right text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Müz. fiyat</th>
                                 <th scope="col" className="px-2 py-1.5 text-center text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Müz. teslimat</th>
                               </>
                             )}
                             {showDecisionColumn && (
                               <th scope="col" className="px-2 py-1.5 text-center text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Karar</th>
                             )}
                             {isResponded && (
                               <th scope="col" className="px-2 py-1.5 text-center text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap w-10">İşlem</th>
                             )}
                           </tr>
                         </thead>
                         <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                           {(currentQuoteDetail.quoteItems ?? []).map((item, idx) => (
                             <tr key={item.id} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50'}>
                               <td className="px-2 py-1 text-gray-900 dark:text-white font-medium whitespace-nowrap">{item.poRequestId ?? '—'}</td>
                               <td className="px-2 py-1 text-gray-900 dark:text-white max-w-[140px]">
                                 <span className="block truncate font-medium text-[11px]" title={item.itemName ?? ''}>{item.itemCode ?? '—'}</span>
                                 <span className="block truncate text-[10px] text-gray-500 dark:text-gray-400" title={item.itemName ?? ''}>{item.itemName ?? '—'}</span>
                               </td>
                               <td className="px-2 py-1 text-right text-gray-900 dark:text-white whitespace-nowrap text-[11px]">
                                 {item.requestedQuantity != null ? Number(item.requestedQuantity).toLocaleString('tr-TR') : '—'}
                               </td>
                               {showOfferedColumns && (
                                 <>
                                   <td className="px-2 py-1 text-right text-gray-900 dark:text-white whitespace-nowrap text-[11px]">
                                     {item.requestedQuantity != null ? Number(item.requestedQuantity).toLocaleString('tr-TR') : '—'}
                                   </td>
                                   <td className="px-2 py-1 text-right text-gray-900 dark:text-white whitespace-nowrap text-[11px]">
                                     {item.requestedPrice != null ? Number(item.requestedPrice).toLocaleString('tr-TR') : '—'}
                                   </td>
                                   <td className="px-2 py-1 text-center text-gray-700 dark:text-gray-300 text-[11px] whitespace-nowrap">
                                     {item.requestedDeliveryDays != null ? item.requestedDeliveryDays : '—'}
                                   </td>
                                 </>
                               )}
                               {showNegotiationColumns && (
                                 <>
                                   <td className="px-2 py-1 text-right text-gray-900 dark:text-white whitespace-nowrap text-[11px]">
                                     {item.offeredQuantity != null ? Number(item.offeredQuantity).toLocaleString('tr-TR') : '—'}
                                   </td>
                                   <td className="px-2 py-1 text-right text-gray-900 dark:text-white whitespace-nowrap text-[11px]">
                                     {item.offeredPrice != null ? Number(item.offeredPrice).toLocaleString('tr-TR') : '—'}
                                   </td>
                                   <td className="px-2 py-1 text-center text-gray-700 dark:text-gray-300 text-[11px] whitespace-nowrap">
                                     {item.offeredDeliveryDays != null ? item.offeredDeliveryDays : '—'}
                                   </td>
                                 </>
                               )}
                               {showDecisionColumn && (
                                 <td className="px-2 py-1 text-center">
                                   <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${
                                     item.decisionStatus === 'Awarded' ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300' :
                                     item.decisionStatus === 'Negotiate' ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300' :
                                     item.decisionStatus === 'Rejected' || item.decisionStatus === 'rejected' ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300' :
                                     item.decisionStatus === 'NotDecided' ? 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300' :
                                     'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                                   }`}>
                                     {getDecisionStatusLabel(item.decisionStatus)}
                                   </span>
                                 </td>
                               )}
                               {isResponded && (
                                 <td className="px-2 py-1 text-center">
                                   {(item.decisionStatus === 'NotDecided' || !item.decisionStatus) && item.id != null ? (
                                     <button
                                       type="button"
                                       onClick={() => openQuoteItemDeleteConfirmation(Number(item.id), item.itemName ?? item.itemCode ?? undefined)}
                                       className="p-1 rounded text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                       title="Teklif kalemini sil"
                                     >
                                       <i className="fas fa-trash-alt text-[10px]"></i>
                                     </button>
                                   ) : (
                                     <span className="text-gray-400 dark:text-gray-500">—</span>
                                   )}
                                 </td>
                               )}
                             </tr>
                           ))}
                         </tbody>
                       </table>
                     </div>
                   </div>
                     );
                   })()}
                 </div>
               )}
             </div>
           </div>
         </div>
       )}

       {/* Teklif kalemi silme onay modalı (sadece süreç = Cevaplandı iken gösterilen sil ikonu için) */}
       <ConfirmationModal
         isOpen={showQuoteItemDeleteConfirmation}
         onClose={() => {
           setShowQuoteItemDeleteConfirmation(false);
           setQuoteItemToDelete(null);
         }}
         onConfirm={confirmQuoteItemDelete}
         title="Teklif Kalemini Sil"
         message={quoteItemToDelete
           ? (quoteItemToDelete.itemLabel
             ? `"${quoteItemToDelete.itemLabel}" teklif kalemini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`
             : 'Bu teklif kalemini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.')
           : 'Bu teklif kalemini silmek istediğinize emin misiniz?'}
         confirmText="Evet, Sil"
         cancelText="İptal"
         type="danger"
         icon="fas fa-trash-alt"
         disabled={isQuoteItemDeleteLoading}
       />

       {/* Tetiği çek onay modalı */}
       <ConfirmationModal
         isOpen={showCheckoutConfirmation}
         onClose={() => setShowCheckoutConfirmation(false)}
         onConfirm={confirmCheckout}
         title="Tetiği Çek"
         message="Bu teklif için tetiği çekmek istediğinize emin misiniz?"
         confirmText="Evet, Tetiği Çek"
         cancelText="İptal"
         type="info"
         icon="fas fa-play"
         disabled={isCheckoutLoading}
       />
     </Layout>
   );
 };

export default QuoteTrackingPage;
